import { z } from 'zod';
import { baseComponentSchema } from '../component-base.schemas';

// Pricing component schemas
export const pricingPlanSchema = z.object({
  id: z.string().uuid('Invalid pricing plan ID').optional(),
  name: z.string().min(1, 'Plan name is required').max(50, 'Plan name is too long'),
  description: z.string().max(200, 'Plan description is too long').optional(),
  price: z.object({
    amount: z.number().min(0, 'Price amount must be non-negative'),
    currency: z.string().min(1, 'Currency is required').max(10, 'Currency code too long'),
    period: z.enum(['month', 'year', 'week', 'day', 'one-time']),
    originalAmount: z.number().min(0, 'Original amount must be non-negative').optional(),
  }),
  features: z
    .array(z.string().min(1, 'Feature cannot be empty').max(200, 'Feature description too long'))
    .min(1, 'At least one feature is required')
    .max(20, 'Too many features'),
  highlighted: z.boolean(),
  highlightLabel: z.string().max(30, 'Highlight label is too long').optional(),
  ctaButton: z.object({
    text: z.string().min(1, 'Button text is required').max(30, 'Button text too long'),
    url: z
      .string()
      .url('Invalid button URL')
      .or(z.string().regex(/^\//, 'URL must be absolute or start with /')),
    style: z.enum(['primary', 'secondary', 'outline', 'ghost']),
    target: z.enum(['_blank', '_self']).optional(),
  }),
  badge: z
    .object({
      text: z.string().min(1, 'Badge text is required').max(15, 'Badge text too long'),
      color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid badge color format')
        .optional(),
      textColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid badge text color format')
        .optional(),
    })
    .optional(),
  order: z.number().int().min(0).optional(),
});

// Pricing custom colors schema - allows overriding theme colors per component instance
export const pricingCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    priceColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    featureColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    checkColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    highlightBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    highlightText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const pricingComponentSchema = baseComponentSchema.extend({
  type: z.literal('pricing'),
  data: z.object({
    title: z.string().max(200, 'Title is too long').optional(),
    subtitle: z.string().max(300, 'Subtitle is too long').optional(),
    description: z.string().max(1000, 'Description is too long').optional(),
    plans: z
      .array(pricingPlanSchema)
      .min(1, 'At least one pricing plan is required')
      .max(6, 'Too many pricing plans'),
    layout: z.enum(['grid', 'list', 'carousel']).default('grid'),
    columns: z.enum(['1', '2', '3', '4']).default('3'),
    alignment: z.enum(['left', 'center', 'right']).default('center'),
    cardStyle: z.enum(['default', 'minimal', 'bordered', 'rounded', 'modern']).default('default'),
    showDescription: z.boolean().default(true),
    showOriginalPrice: z.boolean().default(true),
    showBadges: z.boolean().default(true),
    cardShadow: z.boolean().default(true),
    cardBorder: z.boolean().default(false),
    // DEPRECATED: Use customColors.background instead (kept for backward compatibility)
    backgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid background color format')
      .optional(),
    // DEPRECATED: Use customColors.text instead (kept for backward compatibility)
    textColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid text color format')
      .optional(),
    // DEPRECATED: Use customColors.cardBackground instead (kept for backward compatibility)
    cardBackgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid card background color format')
      .optional(),
    // DEPRECATED: Use customColors.cardText instead (kept for backward compatibility)
    cardTextColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid card text color format')
      .optional(),
    // DEPRECATED: Use customColors.priceColor instead (kept for backward compatibility)
    accentColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid accent color format')
      .optional(),
    // DEPRECATED: Use customColors.highlightBackground instead (kept for backward compatibility)
    highlightBackgroundColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid highlight background color format')
      .optional(),
    // DEPRECATED: Use customColors.highlightText instead (kept for backward compatibility)
    highlightTextColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid highlight text color format')
      .optional(),
    showCurrency: z.boolean().default(true),
    currencyPosition: z.enum(['before', 'after']).default('before'),
    showPeriod: z.boolean().default(true),
    spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),
    hoverEffect: z.enum(['none', 'lift', 'zoom', 'glow']).default('lift'),
    animateOnScroll: z.boolean().default(false),
    animationDelay: z.number().int().min(0).max(2000, 'Animation delay too long').optional(),
    // NEW: Component-specific custom colors that override theme colors
    customColors: pricingCustomColorsSchema,
  }),
});

// Type exports
export type PricingPlan = z.infer<typeof pricingPlanSchema>;
export type PricingComponent = z.infer<typeof pricingComponentSchema>;
